import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ContentCopy, Groups, Link as LinkIcon } from '@mui/icons-material';
import * as adminApi from '@/api/admin';
import type {
  FriendlyStudyInvitation,
  FriendlyStudyPilotMetrics,
  FriendlyStudyInvitationStatus,
  PilotFeedbackItem,
  PilotFeedbackSeverity,
  PilotFeedbackStatus,
} from '@/api/admin';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatDate, formatDateTime } from '@/lib/formatters';
import {
  FRIENDLY_STUDY_CHANNEL_STRATEGY,
  FRIENDLY_STUDY_COHORT_GOAL,
  FRIENDLY_STUDY_EXIT_CRITERIA,
  FRIENDLY_STUDY_PILOT_STEPS,
  FRIENDLY_STUDY_PILOT_WINDOW,
  FRIENDLY_STUDY_PRE_INVITE_CHECKLIST,
} from '@/lib/friendlyStudyPilotPlan';
import { FRIENDLY_STUDY_BENEFIT_SUMMARY } from '@/lib/friendlyStudyBenefit';

export const Route = createFileRoute('/_authenticated/admin/estudios-amigos/')({
  component: FriendlyStudiesAdminPage,
});

const STATUS_META: Record<
  FriendlyStudyInvitationStatus,
  { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }
> = {
  Draft: { label: 'Borrador', color: 'default' },
  Sent: { label: 'Enviada', color: 'primary' },
  Opened: { label: 'Abierta', color: 'warning' },
  Accepted: { label: 'Aceptada', color: 'success' },
  Expired: { label: 'Vencida', color: 'default' },
  Revoked: { label: 'Revocada', color: 'default' },
  Failed: { label: 'Falló email', color: 'error' },
};

const FEEDBACK_STATUS_META: Record<
  PilotFeedbackStatus,
  { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }
> = {
  New: { label: 'Nuevo', color: 'primary' },
  Seen: { label: 'Visto', color: 'default' },
  Analyzing: { label: 'Analizando', color: 'warning' },
  Planned: { label: 'Planificado', color: 'warning' },
  Resolved: { label: 'Resuelto', color: 'success' },
  Dismissed: { label: 'Descartado', color: 'default' },
};

const FEEDBACK_SEVERITY_META: Record<
  PilotFeedbackSeverity,
  { label: string; color: 'default' | 'warning' | 'error' }
> = {
  Idea: { label: 'Idea', color: 'default' },
  Friction: { label: 'Fricción', color: 'warning' },
  Blocker: { label: 'Bloqueo', color: 'error' },
};

type FeedbackDraft = {
  status: PilotFeedbackStatus;
  assignedTo: string;
  tags: string;
  closedNote: string;
};

function FriendlyStudiesAdminPage(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<FriendlyStudyInvitationStatus | ''>('');
  const [feedbackStatus, setFeedbackStatus] = useState<PilotFeedbackStatus | ''>('');
  const [lastLink, setLastLink] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [feedbackDrafts, setFeedbackDrafts] = useState<Record<string, FeedbackDraft>>({});
  const [draft, setDraft] = useState({
    email: '',
    studyName: '',
    contactName: '',
    cuit: '',
    assignedTo: '',
    customMessage: '',
    internalNote: '',
    sendEmail: true,
  });

  const invitationsQ = useQuery({
    queryKey: ['friendly-studies', search, status],
    queryFn: () => adminApi.listFriendlyStudyInvitations({ search: search || undefined, status }),
  });

  const metricsQ = useQuery({
    queryKey: ['friendly-studies', 'metrics'],
    queryFn: adminApi.getFriendlyStudyPilotMetrics,
  });

  const feedbackQ = useQuery({
    queryKey: ['pilot-feedback', feedbackStatus],
    queryFn: () => adminApi.listPilotFeedback({ status: feedbackStatus, take: 25 }),
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['friendly-studies'] });
    void qc.invalidateQueries({ queryKey: ['pilot-feedback'] });
  };
  const onError = (e: unknown): void => setError(toApiError(e).message);

  const createMut = useMutation({
    mutationFn: () => adminApi.createFriendlyStudyInvitation({
      email: draft.email,
      studyName: draft.studyName,
      contactName: clean(draft.contactName),
      cuit: clean(draft.cuit),
      assignedTo: clean(draft.assignedTo),
      customMessage: clean(draft.customMessage),
      internalNote: clean(draft.internalNote),
      sendEmail: draft.sendEmail,
    }),
    onSuccess: (result) => {
      setLastLink(result.acceptanceUrl);
      setLinkCopied(false);
      setDraft({
        email: '',
        studyName: '',
        contactName: '',
        cuit: '',
        assignedTo: '',
        customMessage: '',
        internalNote: '',
        sendEmail: true,
      });
      invalidate();
    },
    onError,
  });

  const resendMut = useMutation({
    mutationFn: (id: string) => adminApi.resendFriendlyStudyInvitation(id),
    onSuccess: (result) => {
      setLastLink(result.acceptanceUrl);
      setLinkCopied(false);
      invalidate();
    },
    onError,
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => adminApi.revokeFriendlyStudyInvitation(id),
    onSuccess: invalidate,
    onError,
  });

  const copyInvitationLink = async (url: string): Promise<void> => {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
      setError('El navegador no permite copiar automáticamente. Copiá el link manualmente desde el aviso.');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
    } catch {
      setError('No pudimos copiar el link automáticamente. Copialo manualmente desde el aviso.');
    }
  };

  const updateFeedbackMut = useMutation({
    mutationFn: ({ id, draft: feedbackDraft }: { id: string; draft: FeedbackDraft }) =>
      adminApi.updatePilotFeedback(id, {
        status: feedbackDraft.status,
        assignedTo: clean(feedbackDraft.assignedTo),
        tags: clean(feedbackDraft.tags),
        closedNote: clean(feedbackDraft.closedNote),
      }),
    onSuccess: (feedback) => {
      setFeedbackDrafts((current) => {
        const next = { ...current };
        delete next[feedback.id];
        return next;
      });
      invalidate();
    },
    onError,
  });

  if (invitationsQ.isLoading) return <LoadingScreen message="Cargando Estudios Amigos…" />;

  const invitations = invitationsQ.data?.items ?? [];
  const metrics = metricsQ.data;
  const accepted = metrics?.acceptedInvitations ?? invitations.filter((i) => i.status === 'Accepted').length;
  const pending = metrics?.pendingInvitations ?? invitations.filter((i) => ['Draft', 'Sent', 'Opened'].includes(i.status)).length;
  const failed = metrics?.failedEmailInvitations ?? invitations.filter((i) => i.status === 'Failed' || i.lastEmailErrorCode).length;
  const totalInvitations = metrics?.totalInvitations ?? invitationsQ.data?.total ?? invitations.length;
  const openFeedback = metrics?.openFeedback ?? feedbackQ.data?.items.length ?? 0;
  const openBlockers = metrics?.openBlockers ?? 0;

  const setFeedbackDraft = (feedback: PilotFeedbackItem, patch: Partial<FeedbackDraft>): void => {
    setFeedbackDrafts((current) => ({
      ...current,
      [feedback.id]: { ...draftFor(feedback, current), ...patch },
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Groups color="primary" /> Estudios Amigos
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Invitaciones a estudios contables de confianza, beneficio 100% bonificado y feedback del piloto en un solo lugar.
        </Typography>
      </Stack>

      {error ? <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert> : null}
      {lastLink ? (
        <Alert
          severity="success"
          onClose={() => {
            setLastLink(null);
            setLinkCopied(false);
          }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<ContentCopy fontSize="inherit" />}
              onClick={() => void copyInvitationLink(lastLink)}
            >
              {linkCopied ? 'Copiado' : 'Copiar'}
            </Button>
          }
          sx={{ mb: 2 }}
        >
          Invitación generada. Link operativo:{' '}
          <Typography component="span" sx={{ fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>
            {lastLink}
          </Typography>
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 2 }}><Kpi label="Invitaciones" value={String(totalInvitations)} /></Grid>
        <Grid size={{ xs: 12, md: 2 }}><Kpi label="Pendientes" value={String(pending)} /></Grid>
        <Grid size={{ xs: 12, md: 2 }}><Kpi label="Aceptadas" value={String(accepted)} /></Grid>
        <Grid size={{ xs: 12, md: 2 }}><Kpi label="1° factura" value={formatRatio(metrics?.acceptedTenantsWithFirstInvoice, accepted, metrics?.invoiceMetricsAvailable)} /></Grid>
        <Grid size={{ xs: 12, md: 2 }}><Kpi label="PDF generado" value={formatRatio(metrics?.acceptedTenantsWithPdfGenerated, accepted, metrics?.invoiceMetricsAvailable)} /></Grid>
        <Grid size={{ xs: 12, md: 2 }}><Kpi label="Bloqueos" value={String(openBlockers)} helper={`${openFeedback} feedback abiertos`} /></Grid>
        <Grid size={{ xs: 12, md: 3 }}><Kpi label="Fallos de email" value={String(failed)} /></Grid>
        <Grid size={{ xs: 12, md: 3 }}><Kpi label="Horas a 1° factura" value={formatHours(metrics)} /></Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Cobertura PDF</Typography>
              {metricsQ.isLoading ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Cargando interacciones…</Typography>
                </Stack>
              ) : metrics?.pdfInteractionMetricsAvailable ? (
                <Typography variant="body2">
                  Vistos {metrics.pdfViewed ?? 0} · descargados {metrics.pdfDownloaded ?? 0} · compartidos {metrics.pdfShared ?? 0} · enviados {metrics.pdfEmailedToCustomer ?? 0} · emisor {metrics.pdfEmailedToIssuer ?? 0}
                </Typography>
              ) : (
                <Alert severity="warning" sx={{ mt: 0.5 }}>
                  No pudimos cargar las interacciones PDF. Revisá el endpoint de métricas del piloto.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, minWidth: 240 }}>
                Plan operativo del piloto
              </Typography>
              <Chip size="small" color="primary" label={`Ventana ${FRIENDLY_STUDY_PILOT_WINDOW}`} />
              <Chip size="small" color="success" variant="outlined" label={FRIENDLY_STUDY_COHORT_GOAL} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Este programa sirve para validar Tilde con estudios contables amigos antes del lanzamiento abierto: invitación, activación, primera operación real y feedback clasificado.
            </Typography>
            <Grid container spacing={1.5}>
              {FRIENDLY_STUDY_PILOT_STEPS.map((step, index) => (
                <Grid key={step.title} size={{ xs: 12, md: 6, lg: index === FRIENDLY_STUDY_PILOT_STEPS.length - 1 ? 12 : 6 }}>
                  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Chip size="small" label={index + 1} color="primary" />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{step.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{step.description}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Antes de mandar mails reales</Typography>
                  <Stack spacing={0.75}>
                    {FRIENDLY_STUDY_PRE_INVITE_CHECKLIST.map((item) => (
                      <Typography key={item} variant="body2" color="text.secondary">• {item}</Typography>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Criterios de salida</Typography>
                  <Stack spacing={0.75}>
                    {FRIENDLY_STUDY_EXIT_CRITERIA.map((criterion) => (
                      <Typography key={criterion} variant="body2" color="text.secondary">• {criterion}</Typography>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Canales de feedback</Typography>
                  <Stack spacing={1}>
                    {FRIENDLY_STUDY_CHANNEL_STRATEGY.map((channel) => (
                      <Box key={channel.title}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{channel.title}</Typography>
                          {channel.recommended ? <Chip size="small" color="success" label="Recomendado" /> : null}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">{channel.description}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Nueva invitación</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            {`Beneficio fijo: ${FRIENDLY_STUDY_BENEFIT_SUMMARY}. No se cobra por PSP y no vence como trial.`}
          </Alert>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth size="small" label="Email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth size="small" label="Nombre del estudio" value={draft.studyName} onChange={(e) => setDraft({ ...draft, studyName: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth size="small" label="Contacto" value={draft.contactName} onChange={(e) => setDraft({ ...draft, contactName: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth size="small" label="CUIT opcional" value={draft.cuit} onChange={(e) => setDraft({ ...draft, cuit: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth size="small" label="Responsable Tilde" value={draft.assignedTo} onChange={(e) => setDraft({ ...draft, assignedTo: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Mensaje personalizado" value={draft.customMessage} onChange={(e) => setDraft({ ...draft, customMessage: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" label="Nota interna" value={draft.internalNote} onChange={(e) => setDraft({ ...draft, internalNote: e.target.value })} />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} alignItems="center">
            <FormControlLabel
              control={<Checkbox checked={draft.sendEmail} onChange={(e) => setDraft({ ...draft, sendEmail: e.target.checked })} />}
              label="Enviar mail ahora"
            />
            <Button
              variant="contained"
              disabled={!draft.email.trim() || !draft.studyName.trim() || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              {createMut.isPending ? <CircularProgress size={18} /> : 'Crear invitación'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <TextField size="small" label="Buscar" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }} />
            <TextField
              select
              size="small"
              label="Estado"
              value={status}
              onChange={(e) => setStatus(e.target.value as FriendlyStudyInvitationStatus | '')}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(STATUS_META).map(([value, meta]) => <MenuItem key={value} value={value}>{meta.label}</MenuItem>)}
            </TextField>
            <Button variant="outlined" onClick={() => invitationsQ.refetch()}>Actualizar</Button>
          </Stack>
          {invitations.length === 0 ? (
            <Alert severity="info">Todavía no hay estudios invitados.</Alert>
          ) : (
            <InvitationsTable
              items={invitations}
              busy={resendMut.isPending || revokeMut.isPending}
              onResend={(id) => resendMut.mutate(id)}
              onRevoke={(id) => revokeMut.mutate(id)}
              onOpenTenant={(id) => navigate({ to: '/admin/tenants/$id', params: { id } })}
            />
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Feedback del piloto</Typography>
              <Typography variant="body2" color="text.secondary">
                Sugerencias enviadas desde el botón de ayuda de la app. Úsalas como tablero liviano para la semana de prueba.
              </Typography>
            </Box>
            <TextField
              select
              size="small"
              label="Estado"
              value={feedbackStatus}
              onChange={(e) => setFeedbackStatus(e.target.value as PilotFeedbackStatus | '')}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(FEEDBACK_STATUS_META).map(([value, meta]) => <MenuItem key={value} value={value}>{meta.label}</MenuItem>)}
            </TextField>
            <Button variant="outlined" onClick={() => feedbackQ.refetch()}>Actualizar</Button>
          </Stack>
          {feedbackQ.isLoading ? (
            <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography>Cargando feedback…</Typography></Stack>
          ) : feedbackQ.data?.items.length ? (
            <FeedbackTable
              items={feedbackQ.data.items}
              drafts={feedbackDrafts}
              busy={updateFeedbackMut.isPending}
              onDraft={setFeedbackDraft}
              onSave={(feedback, feedbackDraft) => updateFeedbackMut.mutate({ id: feedback.id, draft: feedbackDraft })}
            />
          ) : (
            <Alert severity="info">Cuando los invitados usen “Enviar sugerencia”, vas a ver los mensajes acá.</Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function InvitationsTable({
  items,
  busy,
  onResend,
  onRevoke,
  onOpenTenant,
}: {
  items: FriendlyStudyInvitation[];
  busy: boolean;
  onResend: (id: string) => void;
  onRevoke: (id: string) => void;
  onOpenTenant: (id: string) => void;
}): JSX.Element {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Estudio</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell>Responsable</TableCell>
            <TableCell>Fechas</TableCell>
            <TableCell>Mail</TableCell>
            <TableCell align="right">Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((i) => {
            const meta = STATUS_META[i.status];
            return (
              <TableRow key={i.id} hover>
                <TableCell>
                  <Typography sx={{ fontWeight: 700 }}>{i.studyName}</Typography>
                  <Typography variant="caption" color="text.secondary">{i.email}</Typography>
                </TableCell>
                <TableCell><Chip size="small" color={meta.color} label={meta.label} /></TableCell>
                <TableCell>{i.assignedTo || '—'}</TableCell>
                <TableCell>
                  <Typography variant="caption" display="block">Creada: {formatDate(i.createdAt)}</Typography>
                  {i.acceptedAt ? (
                    <Typography variant="caption" display="block" color="text.secondary">Aceptó: {formatDate(i.acceptedAt)}</Typography>
                  ) : null}
                  <Typography variant="caption" display="block" color={i.lastActivityAt ? 'text.secondary' : 'warning.main'}>
                    Actividad: {i.lastActivityAt ? formatDateTime(i.lastActivityAt) : 'sin actividad PDF'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Vence: {formatDate(i.expiresAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" display="block">Envíos: {i.emailSendCount}</Typography>
                  {i.lastEmailErrorCode ? <Chip size="small" color="error" label={i.lastEmailErrorCode} /> : null}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {i.acceptedTenantId ? <Button size="small" onClick={() => onOpenTenant(i.acceptedTenantId!)}>Abrir tenant</Button> : null}
                    <Button
                      size="small"
                      startIcon={<LinkIcon />}
                      disabled={busy || i.status === 'Accepted' || i.status === 'Revoked'}
                      onClick={() => onResend(i.id)}
                    >
                      Reenviar
                    </Button>
                    <Button
                      size="small"
                      color="warning"
                      disabled={busy || i.status === 'Accepted' || i.status === 'Revoked'}
                      onClick={() => onRevoke(i.id)}
                    >
                      Revocar
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function FeedbackTable({
  items,
  drafts,
  busy,
  onDraft,
  onSave,
}: {
  items: PilotFeedbackItem[];
  drafts: Record<string, FeedbackDraft>;
  busy: boolean;
  onDraft: (feedback: PilotFeedbackItem, patch: Partial<FeedbackDraft>) => void;
  onSave: (feedback: PilotFeedbackItem, draft: FeedbackDraft) => void;
}): JSX.Element {
  return (
    <Stack spacing={1.5}>
      {items.map((feedback) => {
        const draft = draftFor(feedback, drafts);
        const statusMeta = FEEDBACK_STATUS_META[draft.status];
        const severityMeta = FEEDBACK_SEVERITY_META[feedback.severity];
        return (
          <Paper key={feedback.id} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Chip size="small" label={severityMeta.label} color={severityMeta.color} />
              <Chip size="small" label={statusMeta.label} color={statusMeta.color} variant="outlined" />
              <Typography variant="caption" color="text.secondary">
                {formatDateTime(feedback.createdAt)} · {feedback.authorEmail} · {feedback.category}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ mb: 1 }}>{feedback.message}</Typography>
            {feedback.pageUrl ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, wordBreak: 'break-all' }}>
                Pantalla: {feedback.pageUrl}
              </Typography>
            ) : null}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
              <TextField
                select
                size="small"
                label="Estado"
                value={draft.status}
                onChange={(e) => onDraft(feedback, { status: e.target.value as PilotFeedbackStatus })}
                sx={{ minWidth: 160 }}
              >
                {Object.entries(FEEDBACK_STATUS_META).map(([value, meta]) => <MenuItem key={value} value={value}>{meta.label}</MenuItem>)}
              </TextField>
              <TextField
                size="small"
                label="Asignado a"
                value={draft.assignedTo}
                onChange={(e) => onDraft(feedback, { assignedTo: e.target.value })}
                sx={{ minWidth: 180 }}
              />
              <TextField
                size="small"
                label="Tags"
                value={draft.tags}
                onChange={(e) => onDraft(feedback, { tags: e.target.value })}
                sx={{ minWidth: 180 }}
              />
              <TextField
                size="small"
                label="Nota de cierre"
                value={draft.closedNote}
                onChange={(e) => onDraft(feedback, { closedNote: e.target.value })}
                sx={{ flex: 1, minWidth: 220 }}
              />
              <Button variant="contained" disabled={busy} onClick={() => onSave(feedback, draft)}>
                Guardar
              </Button>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

function draftFor(feedback: PilotFeedbackItem, drafts: Record<string, FeedbackDraft>): FeedbackDraft {
  return drafts[feedback.id] ?? {
    status: feedback.status,
    assignedTo: feedback.assignedTo ?? '',
    tags: feedback.tags ?? '',
    closedNote: feedback.closedNote ?? '',
  };
}

function Kpi({ label, value, helper }: { label: string; value: string; helper?: string }): JSX.Element {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>{value}</Typography>
        {helper ? <Typography variant="caption" color="text.secondary">{helper}</Typography> : null}
      </CardContent>
    </Card>
  );
}

function formatRatio(value: number | null | undefined, denominator: number, available: boolean | undefined): string {
  if (available === false) return 'sin señal';
  return `${value ?? 0}/${denominator}`;
}

function formatHours(metrics: FriendlyStudyPilotMetrics | undefined): string {
  if (!metrics?.invoiceMetricsAvailable) return 'sin señal';
  if (metrics.averageHoursToFirstInvoice === null) return '—';
  return `${metrics.averageHoursToFirstInvoice} h`;
}

function clean(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
